import React, { FC, Fragment, useState } from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RemoveCircleRoundedIcon from '@mui/icons-material/RemoveCircleRounded';
import { IconButton, Tooltip, Unstable_Grid2 as Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UseMutationOptions } from 'react-query';

import { Alert } from 'components';
import { useNavigate } from 'react-router-dom';

import {
  ExternalShare,
  UpdateExternalShareMutation,
  UpdateExternalShareMutationVariables,
  useUpdateExternalShareMutation,
} from 'api';
import { useNotify } from 'hooks';
import theme from 'theme';
import * as paths from 'paths';
import { ExternalShares } from 'features/admin/types';

enum Action {
  Reject,
  None,
}

export type UpdateExternalShareMutationOptions = UseMutationOptions<
  UpdateExternalShareMutation,
  unknown,
  UpdateExternalShareMutationVariables
>;

interface Props extends ExternalShare {
  updateExternalSharesMutationOptions: UpdateExternalShareMutationOptions;
}

const styles = {
  actions: {
    marginRight: theme.spacing(2),
  },
} as const;

const Actions: FC<Props> = ({ updateExternalSharesMutationOptions, ...rest }) => {
  const { share_id, share_url, user_commentary } = rest;
  const { t } = useTranslation();
  const notify = useNotify();
  const navigate = useNavigate();

  const [action, setAction] = useState<Action>(Action.None);

  const { mutate } = useUpdateExternalShareMutation({
    ...updateExternalSharesMutationOptions,
    onSuccess: () => notify.info(t('admin:contentRejected', { count: 1 })),
  });

  function handleContentApproval() {
    const externalSharesState: ExternalShares = {
      share_url,
      user_commentary,
      share_id,
    };
    sessionStorage.setItem('externalSharesState', JSON.stringify(externalSharesState));
    navigate(paths.postCreate);
    setAction(Action.None);
  }

  function handleContentRemoval() {
    mutate({
      data: { engage_content_created: false },
      share_id,
    });
    setAction(Action.None);
  }

  function renderAlerts() {
    return (
      <Fragment>
        <Alert
          destructive
          open={action === Action.Reject}
          onCancel={() => setAction(Action.None)}
          onConfirm={handleContentRemoval}
        />
      </Fragment>
    );
  }

  function renderApprove() {
    return (
      <Grid>
        <Tooltip title={`${t('common:approve')} & ${t('common:create')}`}>
          <IconButton onClick={handleContentApproval}>
            <CheckCircleRoundedIcon color="inherit" />
          </IconButton>
        </Tooltip>
      </Grid>
    );
  }

  function renderReject() {
    return (
      <Grid>
        <Tooltip title={t('common:reject')}>
          <IconButton onClick={() => setAction(Action.Reject)}>
            <RemoveCircleRoundedIcon color="inherit" />
          </IconButton>
        </Tooltip>
      </Grid>
    );
  }

  return (
    <Fragment>
      {renderAlerts()}
      <Grid wrap="nowrap" container alignItems="center" justifyContent="center" sx={styles.actions}>
        {renderApprove()}
        {renderReject()}
      </Grid>
    </Fragment>
  );
};

export default Actions;
